import { useEffect, useRef } from 'react';
import { Check, Minus } from 'lucide-react';

/**
 * Checkbox — checked and indeterminate, per the GOV360 sheet.
 *
 * A real <input type="checkbox"> does the work (keyboard, form participation,
 * screen readers); the visible box is a sibling driven by `peer-*` variants, so
 * focus and checked state can never drift from the input's actual state.
 *
 * `indeterminate` is a DOM property, not an attribute — React can't set it
 * declaratively, hence the ref effect.
 */
export function Checkbox({
  checked = false,
  indeterminate = false,
  onChange,
  label,
  disabled = false,
  className = '',
  ...rest
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);

  return (
    <label
      className={[
        'inline-flex items-center gap-2 select-none',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        className,
      ].join(' ')}
    >
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="peer sr-only"
        {...rest}
      />
      <span
        className={[
          'grid place-items-center w-4 h-4 shrink-0 rounded-[4px]',
          'border border-bd-strong bg-white text-white',
          'peer-checked:bg-blue peer-checked:border-blue',
          'peer-indeterminate:bg-blue peer-indeterminate:border-blue',
          'peer-focus-visible:outline-2 peer-focus-visible:outline-blue',
          'peer-focus-visible:outline-offset-2',
        ].join(' ')}
        aria-hidden="true"
      >
        {checked ? (
          <Check className="w-3 h-3" strokeWidth={3} />
        ) : indeterminate ? (
          <Minus className="w-3 h-3" strokeWidth={3} />
        ) : null}
      </span>
      {label && <span className="text-[13px] font-medium text-t2">{label}</span>}
    </label>
  );
}
